import * as THREE from 'three';
import type { NodeImplementation } from '@spotar/runtime';
import { applyEaseCurve } from '../helper/easing-helper';
import { runFrameAnimation, stopFrameAnimation } from '../helper/frame-animation-helper';
import { sceneHelper } from '../helper/scene-helper';
import type { Object3D } from 'three';

const SCALE_EPSILON = 1e-6;

type ScaleToData = {
  target: { id: string };
  scale: THREE.Vector3;
  speed?: number;
  curve?: string;
};

function getScaleAnimationKey(object: Object3D): string {
  return `scale:${object.uuid}`;
}

function animateObjectScale(
  object: Object3D,
  targetScale: THREE.Vector3,
  speedUnitsPerSecond: number,
  curve: string | undefined
): Promise<boolean> {
  const animationKey = getScaleAnimationKey(object);
  const start = object.scale.clone();
  const distance = start.distanceTo(targetScale);

  if (distance < SCALE_EPSILON) {
    stopFrameAnimation(animationKey);
    object.scale.copy(targetScale);
    return Promise.resolve(true);
  }

  return runFrameAnimation({
    key: animationKey,
    durationSeconds: distance / speedUnitsPerSecond,
    onProgress: (progress) =>
      object.scale.lerpVectors(start, targetScale, applyEaseCurve(progress, curve)),
    onComplete: () => object.scale.copy(targetScale)
  });
}

export const runScaleToImplementation: NodeImplementation = async (data, _nodeId) => {
  const { target, scale, speed, curve } = data as ScaleToData;
  const object = sceneHelper?.getObjectByUUID(target.id);
  if (!object) {
    console.error(`Object ${target.id} not found`);
    return;
  }

  const speedUnitsPerSecond = speed ?? 1;

  await animateObjectScale(object, scale, speedUnitsPerSecond, curve);
};
